import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/contexts/RoleContext";
import {
  X,
  LogOut,
  LayoutDashboard,
  Trophy,
  Users,
  User,
  MapPin,
  Award,
  FileText,
  BarChart3,
  Building,
  Tags,
  Tag,
  Percent,
  Ticket,
  Shield,
  HelpCircle,
  ShieldCheck,
  BedDouble,
  Trophy as TrophyIcon,
  ImageIcon,
  Megaphone,
  MessageSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";

const navigationItems = [
  {
    id: "dashboard",
    label: "Dashboard",
    href: "/admin-dashboard",
    icon: LayoutDashboard,
  },
  {
    id: "events",
    label: "Events",
    href: "/admin-dashboard/events",
    icon: Trophy,
  },
  {
    id: "circuits",
    label: "Circuits",
    href: "/admin-dashboard/circuits",
    icon: TrophyIcon,
  },
  {
    id: "event-categories",
    label: "Event Categories",
    href: "/admin-dashboard/event-categories",
    icon: Tags,
  },
  {
    id: "discount-policies",
    label: "Discount Policies",
    href: "/admin-dashboard/event-discount-policies",
    icon: Percent,
  },
  {
    id: "teams",
    label: "Teams",
    href: "/admin/teams/merge", // TODO: replace with dedicated teams admin route when built
    icon: Users,
  },
  {
    id: "players",
    label: "Players",
    href: "#", // TODO: no admin players route yet
    icon: User,
  },
  {
    id: "master-teams",
    label: "Master Teams",
    href: "/admin-dashboard/master-teams",
    icon: Shield,
  },
  {
    id: "venues",
    label: "Venues",
    href: "/admin-dashboard/venues",
    icon: MapPin,
  },
  {
    id: "sponsors",
    label: "Sponsors",
    href: "/admin-dashboard/event-sponsors",
    icon: Award,
  },
  {
    id: "coupons",
    label: "Coupons",
    href: "/admin-dashboard/coupons",
    icon: Ticket,
  },
  {
    id: "divisions",
    label: "Divisions",
    href: "/admin-dashboard/divisions",
    icon: Tag,
  },
  {
    id: "constraints",
    label: "Constraints",
    href: "/admin-dashboard/constraints",
    icon: Shield,
  },
  {
    id: "faqs",
    label: "FAQs",
    href: "/admin-dashboard/faqs",
    icon: HelpCircle,
  },
  {
    id: "waivers",
    label: "Waivers",
    href: "/admin-dashboard/waivers",
    icon: ShieldCheck,
  },
  {
    id: "forms",
    label: "Forms & Templates",
    href: "/admin-dashboard/events/registration-forms",
    icon: FileText,
    subItems: [
      { label: "Registration Forms", href: "/admin-dashboard/events/registration-forms" },
      { label: "Packet Management", href: "/admin-dashboard/events/packet-management" },
    ],
  },
  {
    id: "communication-center",
    label: "Communication Center",
    href: "/admin/messages/templates",
    icon: MessageSquare,
    subItems: [
      { label: "Templates", href: "/admin/messages/templates" },
    ],
  },
  {
    id: "hotels",
    label: "Hotels",
    href: "/admin-dashboard/hotels",
    icon: BedDouble,
    requiresRoles: ["hotel_partner", "rlevents_super_admin", "admin"],
  },
  {
    id: "advertising",
    label: "Advertising",
    href: "/admin-dashboard/advertising/assets",
    icon: Megaphone,
    subItems: [
      { label: "Assets", href: "/admin-dashboard/advertising/assets" },
      { label: "Performance", href: "/admin-dashboard/advertising/performance" },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    href: "/admin-dashboard/events/finance",
    icon: BarChart3,
  },
  {
    id: "organization",
    label: "Organization",
    href: "/admin/staff",
    icon: Building,
    subItems: [
      { label: "Staff Management", href: "/admin/staff" },
      { label: "Organizations", href: "/admin/organizations" },
      { label: "Settings", href: "/admin-dashboard/settings" },
    ],
  },
];

const designNavItems = [
  {
    id: "sport-cover-photos",
    label: "Sport Cover Photos",
    href: "/admin-dashboard/sport-cover-photos",
    icon: ImageIcon,
    requiresRoles: ["rlevents_super_admin", "super_admin", "org_admin", "rlevents_tenant_admin"],
  },
];

interface AppSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AppSidebar({ isOpen, onClose }: AppSidebarProps) {
  const location = useLocation();
  const { signOut } = useAuth();
  const { roles } = useRole();

  const isActive = (href: string) => {
    if (href === "#") return false;
    if (href === "/admin-dashboard") return location.pathname === "/admin-dashboard";
    return location.pathname.startsWith(href);
  };

  const isParentActive = (item: any) => {
    if (isActive(item.href)) return true;
    return item.subItems?.some((sub: any) => isActive(sub.href)) || false;
  };

  const visibleItems = navigationItems.filter((item: any) => {
    if (!item.requiresRoles) return true;
    return item.requiresRoles.some((r: string) => roles.includes(r));
  });

  const visibleDesignItems = designNavItems.filter((item: any) => {
    if (!item.requiresRoles) return true;
    return item.requiresRoles.some((r: string) => roles.includes(r));
  });

  const renderNavItem = (item: any) => {
    const active = isParentActive(item);
    const Icon = item.icon;
    return (
      <div key={item.id}>
        <Link
          to={item.href}
          onClick={onClose}
          className={cn(
            "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-150",
            active
              ? "bg-sidebar-primary text-white shadow-sm"
              : "text-sidebar-muted hover:bg-sidebar-accent hover:text-white"
          )}
        >
          <Icon size={20} className={cn("flex-shrink-0", active ? "text-white" : "text-sidebar-muted")} />
          <span>{item.label}</span>
        </Link>
        {/* Sub-items (visible when parent is active) */}
        {item.subItems && active && (
          <div className="ml-10 mt-1 space-y-0.5">
            {item.subItems.map((sub: any) => (
              <Link
                key={sub.href}
                to={sub.href}
                onClick={onClose}
                className={cn(
                  "block rounded-lg px-3 py-2 text-xs font-medium transition-colors",
                  isActive(sub.href)
                    ? "text-white"
                    : "text-sidebar-muted hover:text-white"
                )}
              >
                {sub.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-screen flex flex-col bg-sidebar text-sidebar-foreground shadow-[1px_0_0_0_hsl(0_0%_92%)] transition-transform duration-200 ease-out",
          "w-64 lg:sticky lg:top-0 lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-6 pt-6 pb-8 flex-shrink-0">
          <Link to="/admin-dashboard" className="flex items-center gap-2.5" onClick={onClose}>
            <div className="flex h-9 w-9 items-center justify-center">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <path d="M18 2L8 8v16c0 2 2 4 4 4h12c2 0 4-2 4-4V8l-10-6z" fill="hsl(var(--sidebar-primary))" opacity="0.9" />
                <circle cx="15" cy="12" r="2" fill="white" opacity="0.8" />
                <circle cx="21" cy="12" r="2" fill="white" opacity="0.8" />
                <path d="M12 18h12v1h-12z" fill="white" opacity="0.6" />
              </svg>
            </div>
            <span className="text-xl font-bold text-white tracking-tight">RLevents</span>
          </Link>
          <button onClick={onClose} className="lg:hidden text-sidebar-muted hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col gap-1 px-3 overflow-y-auto scrollbar-thin">
          {visibleItems.map(renderNavItem)}
          {visibleDesignItems.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-sidebar-muted/60 uppercase tracking-wider mt-4 mb-1">
                Design
              </div>
              {visibleDesignItems.map(renderNavItem)}
            </>
          )}
        </nav>

        {/* Bottom section - sign out */}
        <div className="flex-shrink-0 px-3 pb-6 pt-4">
          <button
            onClick={() => signOut?.()}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-sidebar-muted hover:text-white transition-colors"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
}
